// ============================================================================
// Module: modelColors
//
// Shared chart palette + deterministic per-model color, used by every
// dashboard widget that colors something by agent_checkin.model:
// TokenSpendOverTime, TopModelsUsed, TopProjects.
//
// Colors are derived from the model id string itself (a stable hash into
// CHART_COLORS), not from "first encountered" or "biggest consumer" order.
// Those orderings are dataset-dependent — which models show up, and in
// what order, differs by day, by 7-day window, and by widget — so keying
// color off order means the same model could render as a different color
// in TopModelsUsed than in TokenSpendOverTime, or even in the same widget
// on a different day. Hashing the model id directly is the one ordering
// that's guaranteed stable everywhere a model appears, which is the whole
// point: the same model reads as the same color on every card on the
// dashboard.
//
// This repo's --chart-N theme vars store raw HSL components ("H S% L%"),
// not standalone color values — callers get a ready-to-use hsl(...) string
// back from getModelColor, so there's no need to re-wrap it.
// ============================================================================

export const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

// Fixed slots for the model FAMILIES that actually co-occur on one team's
// dashboard in practice (see the public.model_pricing table's
// `family` column for the same family list) — these are the categories a viewer needs to tell apart at a
// glance, so they get guaranteed-distinct colors rather than whatever a
// generic hash happens to produce. A first cut of this hashed the whole raw
// model id (e.g. "claude-sonnet-4-5-20250929") and it silently collided:
// sonnet/opus/fable all landed on chart-1 and haiku/unknown both landed on
// chart-4 (only 5 buckets, hashing on distinguishing detail — the date
// suffix, dash count — the family name itself doesn't determine), so two
// different families shown side by side could render as the same color.
// Fixing that requires the small known set to be enumerated, not hashed.
const FAMILY_COLOR_INDEX: Record<string, number> = {
  opus: 0,
  sonnet: 1,
  haiku: 2,
  fable: 3,
  unknown: 4,
};

const DATE_SUFFIX = /-\d{8}$/;

// Same family extraction modelLabel.ts (and the server-side
// private.resolve_model_rate) use: strip a "claude-" prefix and trailing 8-digit date suffix, and take
// the first remaining dash-separated segment ("claude-opus-4-5-20250929" ->
// "opus"). Two different *versions* of the same family (Sonnet 4.5 vs.
// Sonnet 5) still share a color — the thing this needs to guarantee is
// distinct families not colliding, not every possible model string being
// unique, and a 5-color palette can't give every version its own color
// anyway once more than 5 distinct models are in play.
function parseFamily(model: string): string {
  const cleaned = model.toLowerCase().replace(/^claude-/, "").replace(DATE_SUFFIX, "");
  return cleaned.split("-").filter(Boolean)[0] ?? "";
}

// Tiny stable string hash (DJB2) — fallback for a family this table hasn't
// seen yet (a brand-new model family), so a color still gets picked
// deterministically instead of defaulting to index 0 for everything unknown.
function hashString(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Deterministic color for a model id (or the "unknown" bucket key) — same
 * input always returns the same color, regardless of what else is being
 * rendered alongside it or in what order. Known families (opus/sonnet/
 * haiku/fable/unknown) get fixed, mutually-distinct slots; anything else
 * falls back to a hash of the family name.
 */
export function getModelColor(model: string): string {
  const family = model === "unknown" ? "unknown" : parseFamily(model);
  const index =
    family in FAMILY_COLOR_INDEX
      ? FAMILY_COLOR_INDEX[family]
      : hashString(family || model) % CHART_COLORS.length;
  return CHART_COLORS[index];
}
